// ─────────────────────────────────────────────────────────────────────────────
// StreamingSection, CD's Spotify hero platform card: KPI row (monthly listeners,
// followers, editorial, total streams), the "Top 10 songs · 30 days" list, and
// the "Top playlists" placement list. KPIs + songs come from /api/proxy/stats
// (DashboardStats.spotify); playlists from /api/label/artist/{id}/music
// (PlaylistPlacement[]). Each sub-panel renders CD's empty state when its data is
// absent (Connect Spotify + Songstats / No playlist placements yet).
// ─────────────────────────────────────────────────────────────────────────────

import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { SpotifyStats, PlaylistPlacement, StatTrack } from '../../lib';
import { fetchPlaylistArtwork } from '../../lib';
import { fmt, fmtReach, daysAgoLabel } from './format';
import { BrandIcon, BRAND_ICON } from './glyphs';

export interface StreamingSectionProps {
  readonly spotify: SpotifyStats | undefined;
  readonly topPlaylists: ReadonlyArray<PlaylistPlacement>;
  /**
   * The band's data id (X-Band-Id), User.band_user_id || User.id. Threaded down
   * to PlaylistCover so its worker artwork fetch carries the SAME band id the
   * playlist fetch (and the legacy roster artHeaders) uses.
   */
  readonly bandId: string;
}

// CD seeds each top-song bar with an arbitrary --bar colour; we cycle CD's exact
// palette (taken verbatim from dashboard.html) so the visual matches.
const BAR_COLORS = [
  '#2f6f7e', '#9aa845', '#3a4ed8', '#2c4a52', '#243038',
  '#7d6a3a', '#5b86c9', '#6b4a39', '#2c4a52', '#d2691e',
];

/** Read a track's stream count. The music endpoint composes per-track streams in
 *  `lifetime_streams` (from SPS streamCount), prefer that; fall back to the raw
 *  SPS field, then the 28-day window, then the legacy streams/value keys. */
function trackStreams(t: { lifetime_streams?: number; streamCount?: number; streams_28d?: number; streams?: number; value?: number }): number {
  return Number(t.lifetime_streams ?? t.streamCount ?? t.streams ?? t.streams_28d ?? t.value ?? 0);
}

/**
 * Spotify track artwork precedence, mirrors lib/dashboard.ts trackArtwork() and
 * dashboard-wiring.js:2265: image || artwork || image_url || album_image ||
 * cover_art. '' when the track carries no artwork.
 */
function trackArtwork(t: StatTrack): string {
  return t.image || t.artwork || t.image_url || t.album_image || t.cover_art || '';
}

/**
 * Song album art for a `.tsong__art` slot. Renders an <img> over CD's striped
 * placeholder; on load-failure (or no art) the <img> removes itself so the
 * striped placeholder stays, never a black/blank square (matches the playlist
 * cover fallback contract). The placeholder lives in the parent span's CSS, so
 * this only adds the image when it actually loads.
 */
function SongArt({ url }: { readonly url: string }): JSX.Element {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <span className="tsong__art" aria-hidden="true"></span>;
  return (
    <span className="tsong__art" aria-hidden="true">
      <img
        className="tsong__art-img"
        src={url}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </span>
  );
}

// ── PlaylistCover, lazy playlist artwork hydration ───────────────────────────
//
// Mirrors roster/js/music-wiring.js:740-902 (the working roster renderer):
//   1. Candidate "direct URL": p.image_url that does NOT contain 'spotifycdn.com'
//      (Spotify's authenticated CDN image-cdn-ak.spotifycdn.com 403s cross-origin
//      → treat as missing).
//   2. PREFLIGHT any candidate via `new Image()`, onload → use it; onerror →
//      fall through. Stops a broken URL leaving a blank square; CD's striped
//      placeholder stays until a real image loads.
//   3. On failure / no direct URL, for spotify rows with a playlist_id, hit the
//      worker (fetchPlaylistArtwork → /api/spotify/playlist-artwork) with the
//      band id, then preflight + use its i.scdn.co url.
//   4. CACHE the resolved worker url in localStorage 7d, keyed `ac_pl_art_<pid>`
//      ({t:<ms>, u:<url>}), same shape as readArtCache/writeArtCache
//      (music-wiring.js:815-828). Cache checked first; a cached-url preflight
//      failure drops the entry and re-fetches.

const ART_CACHE_PREFIX = 'ac_pl_art_';
const ART_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** readArtCache mirror, returns a fresh cached url or null. */
function readArtCache(pid: string): string | null {
  try {
    const raw = localStorage.getItem(ART_CACHE_PREFIX + pid);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { t?: number; u?: string };
    if (parsed && parsed.t && Date.now() - parsed.t < ART_CACHE_TTL_MS && parsed.u) {
      return parsed.u;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** writeArtCache mirror, store {t, u}. */
function writeArtCache(pid: string, url: string): void {
  try {
    localStorage.setItem(ART_CACHE_PREFIX + pid, JSON.stringify({ t: Date.now(), u: url }));
  } catch {
    /* ignore */
  }
}

/** dropArtCache, remove a stale entry after a cached-url preflight failure. */
function dropArtCache(pid: string): void {
  try {
    localStorage.removeItem(ART_CACHE_PREFIX + pid);
  } catch {
    /* ignore */
  }
}

/**
 * paintArt mirror, preflight `url` via a hidden Image(). Resolves true only if
 * the image actually loads, so a broken URL never commits (the placeholder
 * stays). Aborts cleanly if the component unmounts mid-flight.
 */
function preflight(url: string, alive: () => boolean): Promise<boolean> {
  return new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve(alive());
    probe.onerror = () => resolve(false);
    probe.src = url;
  });
}

/**
 * A playlist row's direct cover URL, if any. We do NOT pre-filter by hostname:
 * the roster renderer (music-wiring.js / MusicStreamingSection) shows the SAME
 * `image_url` directly and it loads fine, so blanket-rejecting `spotifycdn.com`
 * here left the dashboard covers blank while the roster showed them. The
 * preflight() below is the real gate, a URL that genuinely 403s fails the probe
 * and falls through to the worker artwork fetch; a loadable one is used.
 */
function usableDirectUrl(p: PlaylistPlacement): string | null {
  const u = p.image_url;
  return u && typeof u === 'string' ? u : null;
}

/**
 * One `.tpl__art` cover that hydrates independently: cache → direct URL →
 * worker fetch, each preflighted, with the resolved worker url cached 7d. Keeps
 * CD's striped placeholder until a real image loads (no black square).
 */
function PlaylistCover({
  playlist,
  bandId,
}: {
  readonly playlist: PlaylistPlacement;
  readonly bandId: string;
}): JSX.Element {
  const [coverUrl, setCoverUrl] = useState<string>('');
  // Guard against setState after unmount across the async preflight/fetch chain.
  const aliveRef = useRef(true);

  const pid = playlist.playlist_id != null ? String(playlist.playlist_id) : '';
  const source = String(playlist.source || '');
  const directUrl = usableDirectUrl(playlist);

  useEffect(() => {
    aliveRef.current = true;
    const alive = () => aliveRef.current;
    let cancelled = false;

    async function hydrate(): Promise<void> {
      // Only spotify rows resolve via the worker; non-spotify can still use a
      // usable direct URL (mirrors the roster `else if (usableImage)` branch).
      const isSpotify = source === 'spotify';

      // 1) Cached worker url first (spotify rows with a pid). Trust it, but still
      //    preflight; a failed preflight drops the cache and falls through.
      if (isSpotify && pid) {
        const cached = readArtCache(pid);
        if (cached) {
          if (await preflight(cached, alive)) {
            if (!cancelled && alive()) setCoverUrl(cached);
            return;
          }
          dropArtCache(pid);
        }
      }

      // 2) Direct URL (preflight; failure → fall through to worker).
      if (directUrl) {
        if (await preflight(directUrl, alive)) {
          if (!cancelled && alive()) setCoverUrl(directUrl);
          return;
        }
      }

      // 3) Worker fetch for spotify rows with a playlist_id.
      if (isSpotify && pid) {
        const url = await fetchPlaylistArtwork(pid, bandId);
        if (url && (await preflight(url, alive))) {
          writeArtCache(pid, url);
          if (!cancelled && alive()) setCoverUrl(url);
          return;
        }
      }
      // Nothing resolved → leave the placeholder (coverUrl stays '').
    }

    void hydrate();
    return () => {
      cancelled = true;
      aliveRef.current = false;
    };
    // Re-run if the row identity or band id changes.
  }, [pid, source, directUrl, bandId]);

  return (
    <span className="tpl__art" aria-hidden="true">
      {coverUrl && (
        <img className="tpl__art-img" src={coverUrl} alt="" loading="lazy" />
      )}
    </span>
  );
}

function editorialCount(spotify: SpotifyStats | undefined): number | null {
  const n = spotify?.ss_playlists_editorial_current;
  return typeof n === 'number' && n > 0 ? n : null;
}

export function StreamingSection({ spotify, topPlaylists, bandId }: StreamingSectionProps): JSX.Element {
  const monthly = spotify?.monthly_listeners ?? null;
  const followers = spotify?.followers ?? null;
  const streamsTotal = spotify?.streams_total ?? null;
  const editorial = editorialCount(spotify);

  const tracks = (spotify?.top_tracks ?? []).slice(0, 10);
  const hasSongs = tracks.length > 0;
  const maxStreams = hasSongs ? Math.max(...tracks.map(trackStreams), 1) : 1;

  const hasPlaylists = topPlaylists.length > 0;
  const totalReach = topPlaylists.reduce((sum, p) => sum + (Number(p.followers) || 0), 0);
  /* playlists-empty-copy 2026-07-18 (Ryan/Mae): the empty panel told a fully
     CONNECTED band to "Connect Spotify + Songstats in Settings", wrong
     instructions that read as their mistake or our breakage. If ANY Spotify
     stat is flowing, the connection works and the truthful state is "no
     current placements on file" (often vendor-side backfill for a newly
     tracked artist). Only tell an actually-unconnected band to connect. */
  const spotifyFlowing = (monthly != null && monthly > 0) || (followers != null && followers > 0) || hasSongs;

  return (
    <article className="card platform-card pc--spotify" id="platform-spotify">
      <header className="platform-card__head">
        <span className="pc-icon pc-icon--brand">
          <BrandIcon file={BRAND_ICON.spotify} className="pc-icon__img" />
        </span>
        <h4 className="platform-card__name">Spotify</h4>
      </header>

      <div className="pc-kpis">
        <div>
          <p className="kpi__label">Monthly listeners</p>
          <p className="kpi__value">{monthly != null && monthly > 0 ? fmt(monthly) : '-'}</p>
        </div>
        <div>
          <p className="kpi__label">Followers</p>
          <p className="kpi__value">{followers != null && followers > 0 ? fmt(followers) : '-'}</p>
        </div>
        <div>
          <p className="kpi__label">Editorial</p>
          <p className={'kpi__value' + (editorial != null ? '' : ' kpi__value--muted')}>
            {editorial != null ? fmt(editorial) : '-'}
          </p>
        </div>
        <div>
          <p className="kpi__label">Total streams</p>
          <p className="kpi__value">{streamsTotal != null && streamsTotal > 0 ? fmt(streamsTotal) : '-'}</p>
        </div>
      </div>

      <div className="pc-detail">
        {/* Top 10 songs */}
        <div className="top-songs">
          <p className="subhead">Top 10 songs · 30 days</p>
          {hasSongs ? (
            <ol className="top-songs__list">
              {tracks.map((t, i) => {
                const streams = trackStreams(t);
                const pct = Math.max(0, Math.round((streams / maxStreams) * 100));
                const title = t.name || t.title || 'Untitled';
                const art = trackArtwork(t);
                return (
                  <li className="tsong" key={String(t.id ?? t.track_id ?? title) + i}>
                    <SongArt url={art} />
                    <div className="tsong__main">
                      <p className="tsong__title">{title}</p>
                      <span className="tsong__track">
                        <span
                          className="tsong__fill"
                          style={{
                            ['--pct' as string]: pct + '%',
                            ['--bar' as string]: BAR_COLORS[i % BAR_COLORS.length],
                          }}
                        ></span>
                      </span>
                    </div>
                    <span className="tsong__count">{fmt(streams)}</span>
                  </li>
                );
              })}
            </ol>
          ) : (
            // ── EMPTY (dashboard-empty.html) ──
            <div className="empty-panel">
              <p className="empty-note">Connect Spotify + Songstats to rank your top songs.</p>
            </div>
          )}
        </div>

        {/* Top playlists */}
        <div className="tpl">
          <p className="subhead">Top playlists</p>
          {hasPlaylists ? (
            <>
              <p className="tpl__sub">
                {topPlaylists.length} placements · ranked by recency · {fmtReach(totalReach)} total reach
              </p>
              <div className="tpl__list">
                {topPlaylists.map((p, i) => (
                  <div className="tpl__row" key={String(p.playlist_id ?? p.name) + i}>
                    <PlaylistCover playlist={p} bandId={bandId} />
                    <div className="tpl__main">
                      <p className="tpl__name">{p.name}</p>
                      <div className="tpl__tags">
                        <span className="tpl__badge tpl__badge--spotify">
                          {p.source ? p.source.charAt(0).toUpperCase() + p.source.slice(1) : 'Spotify'}
                        </span>
                        {p.editorial && <span className="tpl__badge tpl__badge--editorial">Editorial</span>}
                        {p._days_since_added != null && (
                          <span className="tpl__age">{daysAgoLabel(p._days_since_added)}</span>
                        )}
                        {p.track_name && (
                          <span className="tpl__feat">
                            featuring <b>{p.track_name}</b>
                            {p.position != null ? ' · #' + p.position : ''}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="tpl__followers">
                      <span className="tpl__fnum">{p.followers != null ? fmt(p.followers) : '-'}</span>
                      <span className="tpl__flabel">Followers</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            // ── EMPTY (dashboard-empty.html), copy depends on connection state ──
            <div className="playlists-empty">
              <p className="playlists-empty__title">No playlist placements yet</p>
              <p className="playlists-empty__body">
                {spotifyFlowing
                  ? 'No current playlist placements on file. Placements are tracked by Songstats and can take a day or two to appear for newly added artists, they\u2019ll show up here automatically.'
                  : 'Connect Spotify in Settings and playlist placements will appear here.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
